import { ChangeEvent } from 'react';
import { Link, Image } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ImageUploader } from '@/components/ui/image-uploader';
import { ProductFormData } from './ProductForm';

interface FormFieldsProps {
  formData: ProductFormData;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleImageUploaded: (imageUrl: string) => void;
}

export function FormFields({ 
  formData, 
  handleChange, 
  handleImageUploaded 
}: FormFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="name">Product Name*</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Travel Backpack"
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="category">Category</Label>
        <Input
          id="category"
          name="category"
          value={formData.category || ''}
          onChange={handleChange}
          placeholder="e.g., Gear, Clothing, Electronics"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="price">Price</Label>
        <div className="flex">
          <div className="w-16 flex items-center justify-center border border-r-0 rounded-l-md bg-muted">
            <span className="text-sm">$</span>
          </div>
          <Input
            id="price"
            name="price"
            type="number"
            value={formData.price || ''}
            onChange={handleChange}
            placeholder="29.99"
            className="rounded-l-none"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="affiliate_link">Affiliate Link*</Label>
        <div className="flex rounded-md">
          <div className="relative flex items-center">
            <Link className="absolute left-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="affiliate_link"
              name="affiliate_link"
              value={formData.affiliate_link}
              onChange={handleChange}
              placeholder="https://example.com/product?ref=your-affiliate-id"
              className="pl-9"
              required
            />
          </div>
        </div>
      </div>
      
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          placeholder="Brief description of the product and why you recommend it"
          rows={3}
        />
      </div>
      
      <div className="space-y-2 md:col-span-2">
        <ImageUploader
          onImageUploaded={handleImageUploaded}
          currentImage={formData.image_url}
          label="Product Image"
        />
      </div>
    </div>
  );
}
